import type { FormEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppHeader,
  BottomNavigation,
  CaseImageProvider,
  CaseDetailScreen,
  CasesScreen,
  LoginScreen,
  NowScreen,
  PasswordRecoveryScreen,
  PipelineScreen,
  SettingsScreen,
  CallFirstPrompt,
  TodayScreen
} from "./components";
import { toInputDate } from "./date";
import { caseContextLabel, nextStepLabel } from "./format";
import {
  createOwnerSaleFollowUpDraft,
  getCurrentOwnerSaleSequenceStep,
  getNextOwnerSaleSequenceStep,
  getOwnerSaleSequenceStep,
  isOwnerSaleSequenceActive,
  isOwnerSaleSequenceEnded
} from "./ownerSaleSequence";
import { useCloudCaseSync } from "./lib/useCloudCaseSync";
import { analyzeMeetingDebrief, analyzeMeetingDebriefAudio, SupabaseRequestError } from "./lib/supabase";
import { usePushNotifications } from "./lib/usePushNotifications";
import { MarketAnalysisContext, useMarketAnalysisSync } from "./features/market/sync/useMarketAnalysisSync";
import { configuredPublicMarketProvider } from "./features/market/data/publicMarketSourcePolicy";
import { realityRadarXlsxProvider } from "./features/market/data/realityRadar/RealityRadarXlsxProvider";
import { MarketLocationProvider } from "./features/market/location/MarketLocationContext";
import { normalizeLocationText } from "./features/market/location/locationSearch";
import {
  canAddMissingNextStep,
  caseLeadState,
  caseStateCounts,
  isActiveBusinessCase,
  isArchivedCase,
  isAgendaExcludedCase,
  isClosedLostCase,
  isExcludedCase,
  isLiveCase,
  isWaitingCase,
  restoreClosedLostCase
} from "./caseLifecycle";
import { canStartListingWorkflow, startListingWorkflow, stopListingWorkflow } from "./listingWorkflow";
import {
  compareNextActions,
  completeCaseNextAction,
  completeCaseNextActionWithManualNext,
  nextActionBucket
} from "./nextActionEngine";
import { aggregateShotScores, allCaseShotScores, followUpActivityType, shotScoreValue } from "./shotScore";
import {
  addMarketStatsSnapshot,
  listedMarketTimeline,
  milestoneMarketTimeline,
  priceChangedMarketTimeline
} from "./features/marketTimeline/domain";
import type { MarketStatsInput } from "./features/marketTimeline/types";
import { clearCases, loadCases, saveCases } from "./storage";
import {
  activateCompletedEventDebrief,
  getPendingRequiredDebrief,
  incompleteDebriefs,
  isCaseWorkflowGatedByDebrief,
  isCurrentFollowUpDebrief,
  isDebriefPurpose,
  reconcileCaseDebrief,
  resolveDebriefWithoutCompletion
} from "./debrief";
import { DebriefInbox, DebriefSheet, type DebriefItem, type DebriefShotScoreReview } from "./debriefComponents";
import { meetingPlaybookContext } from "./meetingPlaybook";
import { applyManualMeetingOutcome, type MeetingOutcome } from "./meetingOutcome";
import {
  completeReservationDealWorkflow,
  extendReservationWorkflow,
  isReservationControl,
  resolveReservationWorkflow,
  startReservationWorkflow,
  type ReservationOutcome
} from "./reservationWorkflow";
import { CaseDebriefGateScreen } from "./DebriefGateCard";
import {
  type CallOutcome,
  type DebriefAnalysis,
  type DebriefCrmFact,
  type DebriefNotRequiredReason,
  type CaseStage,
  type FollowUp,
  type OwnerSaleSequenceEndReason,
  type RealEstateCase,
  type TimelineActivity
} from "./types";
import type {
  CaseListFilter,
  CaseEditDraft,
  ClosingDraft,
  FollowUpCompletion,
  NewCaseDraft,
  NextStepDraft,
  OwnerSaleContactDraft,
  OwnerSaleEndDraft,
  OutcomeConfigMap,
  OutcomeDraft,
  PropertyFilter,
  RejectionDraft,
  SoldDraft,
  Screen,
  ShotScoreDraft,
  TodayFilter,
  TransactionFilter
} from "./uiTypes";
import { applyWaitingOutcome, excludeCase, moveCaseToWaiting, type WaitingOutcome } from "./waitingWorkflow";

const outcomeConfig: OutcomeConfigMap = {
  Nedvihol: {
    status: "Nedvihol",
    stage: "Prvý cold call",
    defaultPurpose: "Opakovať prvý cold call",
    nextStep: "Opakovať prvý cold call",
    requiresFollowUp: true
  },
  "Dohodnuté stretnutie": {
    status: "Stretnutie dohodnuté",
    stage: "Stretnutie",
    defaultPurpose: "Stretnutie s majiteľom",
    nextStep: "Stretnutie s klientom",
    requiresFollowUp: true
  },
  "Zavolať neskôr": {
    status: "Zavolať neskôr",
    stage: "Follow-up",
    defaultPurpose: "Zavolať neskôr podľa dohody",
    nextStep: "Zavolať neskôr",
    requiresFollowUp: true
  },
  Nezáujem: {
    status: "Čakajúci",
    stage: "Čakajúci",
    defaultPurpose: "",
    nextStep: "Rozhodnúť o čakaní alebo vyradení",
    requiresFollowUp: false
  },
  "Rozmyslí si": {
    status: "Rozmýšľa",
    stage: "Follow-up",
    defaultPurpose: "Overiť rozhodnutie klienta",
    nextStep: "Overiť rozhodnutie klienta",
    requiresFollowUp: true
  },
  "Predáva sám": {
    status: "Predáva sám",
    stage: "Follow-up",
    defaultPurpose: "1. follow-up - majiteľ predáva sám",
    nextStep: "1. follow-up - majiteľ predáva sám",
    requiresFollowUp: true
  }
};

const terminalStages: CaseStage[] = ["Predané", "Uzavretý", "Vyradený", "Stratené"];

const ownerSaleEndState: Record<
  OwnerSaleSequenceEndReason,
  {
    stage: CaseStage;
    status: RealEstateCase["status"];
    nextStep: string;
  }
> = {
  "Získaný nábor": {
    stage: "Aktívne",
    status: "Aktívne",
    nextStep: "Pokračovať v nábore a stratégii predaja"
  },
  "Nechce ďalší kontakt": {
    stage: "Vyradený",
    status: "Vyradený",
    nextStep: "Bez ďalšieho kroku"
  },
  "Nehnuteľnosť predaná": {
    stage: "Vyradený",
    status: "Vyradený",
    nextStep: "Bez ďalšieho kroku"
  },
  "Kontakt nedostupný": {
    stage: "Vyradený",
    status: "Vyradený",
    nextStep: "Bez ďalšieho kroku"
  },
  "Sekvencia ukončená manuálne": {
    stage: "Vyradený",
    status: "Vyradený",
    nextStep: "Bez ďalšieho kroku"
  }
};

const buildOwnerSaleTimelineBody = (
  stepNumber: number,
  result: string,
  note: string,
  followUp: FollowUp | undefined,
  endReason?: OwnerSaleSequenceEndReason
) => {
  const noteText = note ? ` Poznámka: ${note}` : "";

  if (followUp) {
    return `${stepNumber}. follow-up: výsledok hovoru - ${result}.${noteText} Nový ďalší krok: ${nextStepLabel(followUp)}.`;
  }

  return `${stepNumber}. follow-up: výsledok hovoru - ${result}.${noteText} Sekvencia ukončená: ${
    endReason ?? "Sekvencia ukončená manuálne"
  }.`;
};

const createEndedOwnerSaleSequence = (
  caseItem: RealEstateCase,
  endReason: OwnerSaleSequenceEndReason,
  endedAt: string
): RealEstateCase["ownerSaleSequence"] => {
  if (!caseItem.ownerSaleSequence) {
    return undefined;
  }

  return {
    ...caseItem.ownerSaleSequence,
    status: "Ukončená",
    currentStep: Math.max(1, Math.min(4, caseItem.ownerSaleSequence.currentStep || 1)),
    completedContacts: Math.max(0, Math.min(4, caseItem.ownerSaleSequence.completedContacts || 0)),
    endReason,
    endedAt
  };
};

const createId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createActivity = (
  activity: Omit<TimelineActivity, "id" | "createdAt">,
  createdAt = new Date().toISOString()
): TimelineActivity => ({
  id: createId("activity"),
  createdAt,
  ...activity
});

const createEmptyDraft = (): NewCaseDraft => {
  const nextHour = new Date();
  nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);

  return {
    contactName: "",
    phone: "",
    email: "",
    transactionType: "Predaj",
    propertyAddress: "",
    city: "",
    cityNormalized: "",
    citySource: undefined,
    municipalityId: undefined,
    street: "",
    streetNormalized: "",
    streetSource: undefined,
    streetId: undefined,
    locality: "",
    propertyType: "Byt",
    apartmentRooms: "2-izbový",
    cooperationType: "unspecified",
    area: "",
    askingPrice: "",
    recommendedPrice: "",
    commissionPotential: "",
    followUpDate: toInputDate(nextHour),
    followUpTime: nextHour.toTimeString().slice(0, 5),
    followUpPurpose: "Prvý cold call",
    notes: ""
  };
};

const isActiveCase = (caseItem: RealEstateCase) =>
  isActiveBusinessCase(caseItem) && !terminalStages.includes(caseItem.stage) && !isClosedLostCase(caseItem);

const followUpBucket = (caseItem: RealEstateCase, now: Date) => {
  if (isCaseWorkflowGatedByDebrief(caseItem, now)) return "Neaktívne" as const;
  if ((!isActiveCase(caseItem) && !isWaitingCase(caseItem)) || (isOwnerSaleSequenceEnded(caseItem) && !caseItem.followUp)) return "Neaktívne" as const;
  return nextActionBucket(caseItem, now);
};

const sortByPriorityAndTime = (now: Date) => (a: RealEstateCase, b: RealEstateCase) => {
  return compareNextActions(a, b, now);
};

const matchesCaseListFilter = (caseItem: RealEstateCase, filter: CaseListFilter) => {
  if (filter === "Všetky") {
    return isLiveCase(caseItem);
  }

  if (filter === "Aktívne") {
    return isActiveCase(caseItem);
  }

  if (filter === "Čakajúce") {
    return isWaitingCase(caseItem);
  }

  if (filter === "HOT") {
    return isLiveCase(caseItem) && caseItem.priority === "HOT";
  }

  if (filter === "Follow-up") {
    return isLiveCase(caseItem) && (caseItem.stage === "Follow-up" || caseItem.stage === "Rozmýšľa");
  }

  if (filter === "Stretnutia") {
    return isLiveCase(caseItem) && caseItem.stage === "Stretnutie";
  }

  if (filter === "Archív") {
    return isArchivedCase(caseItem);
  }

  return isActiveCase(caseItem) && !caseItem.followUp;
};

function App() {
  const [cases, setCases] = useState<RealEstateCase[]>(loadCases);
  const {
    authReady,
    cloudConfigured,
    passwordRecovery,
    session,
    syncStatus,
    login,
    logout,
    updatePassword,
    completePasswordRecovery,
    storePushSubscription,
    removePushSubscription,
    getPushSubscriptionStatus,
    requestTestPush,
    adoptSession,
    queueAllDeletions,
    queueDeletion
  } = useCloudCaseSync(cases, setCases);
  const marketAnalysis = useMarketAnalysisSync(session, adoptSession, configuredPublicMarketProvider, realityRadarXlsxProvider);
  const {
    status: notificationStatus,
    permission: notificationPermission,
    deviceRegistered: notificationDeviceRegistered,
    lastSuccessfulPushAt,
    lastFailureAt: notificationLastFailureAt,
    lastErrorCategory: notificationLastErrorCategory,
    isInstalledPwa,
    isIosDevice,
    isWorking: notificationsWorking,
    error: notificationError,
    message: notificationMessage,
    enableNotifications,
    disableNotifications,
    sendTestNotification,
    disconnectSubscription
  } = usePushNotifications({
    enabled: cloudConfigured,
    userId: session?.user.id,
    storeSubscription: storePushSubscription,
    removeSubscription: removePushSubscription,
    getSubscriptionStatus: getPushSubscriptionStatus,
    requestTestPush
  });
  const [screen, setScreen] = useState<Screen>("now");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [todayFilter, setTodayFilter] = useState<TodayFilter>("Všetko");
  const [caseFilter, setCaseFilter] = useState<CaseListFilter>("Aktívne");
  const [transactionFilter, setTransactionFilter] = useState<TransactionFilter>("Predaj");
  const [propertyFilter, setPropertyFilter] = useState<PropertyFilter>("Byt");
  const [isCreating, setIsCreating] = useState(false);
  const [newCaseDraft, setNewCaseDraft] = useState<NewCaseDraft>(createEmptyDraft);
  const [outcomeDraft, setOutcomeDraft] = useState<OutcomeDraft | null>(null);
  const [callPromptCaseId, setCallPromptCaseId] = useState<string | null>(null);
  const [nextStepIntentId, setNextStepIntentId] = useState<string | null>(null);
  const [waitingResultIntentId, setWaitingResultIntentId] = useState<string | null>(null);
  const [debriefIntent, setDebriefIntent] = useState<{ caseId: string; reminderId: string } | null>(null);
  const [debriefBusy, setDebriefBusy] = useState(false);
  const [debriefError, setDebriefError] = useState<string>();
  const [debriefConfirmation, setDebriefConfirmation] = useState<string>();
  const [dataWipeStep, setDataWipeStep] = useState(0);
  const [operationalNow, setOperationalNow] = useState(() => new Date());
  const hasMountedRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  useEffect(() => {
    const timer = window.setInterval(() => setOperationalNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setCases((currentCases) => {
      let changed = false;
      const next = currentCases.map((caseItem) => {
        const reconciled = reconcileCaseDebrief(caseItem, operationalNow);
        if (reconciled !== caseItem) changed = true;
        return reconciled;
      });
      return changed ? next : currentCases;
    });
  }, [cases, operationalNow]);

  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    saveCases(cases);
  }, [cases]);

  useEffect(() => {
    if (cases.length === 0 && selectedId) {
      setSelectedId(null);
      return;
    }

    if (!selectedId && cases.length > 0) {
      setSelectedId(cases[0].id);
    }
  }, [cases, selectedId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [screen, selectedId]);

  const today = toInputDate(operationalNow);

  const selectedCase = useMemo(
    () => cases.find((caseItem) => caseItem.id === selectedId) ?? cases[0],
    [cases, selectedId]
  );
  const callPromptCase = cases.find((caseItem) => caseItem.id === callPromptCaseId && caseItem.phone.trim()) ?? null;
  const shotScoreAnalytics = useMemo(() => aggregateShotScores(allCaseShotScores(cases), 30), [cases]);
  const lifecycleCounts = useMemo(() => caseStateCounts(cases), [cases]);
  const pendingDebriefs = useMemo(() => incompleteDebriefs(cases, operationalNow), [cases, operationalNow]);
  const selectedPendingRequiredDebrief = useMemo(
    () => selectedCase ? getPendingRequiredDebrief(selectedCase, operationalNow) : undefined,
    [operationalNow, selectedCase]
  );
  const activeDebrief = useMemo<DebriefItem | undefined>(() => {
    if (!debriefIntent) return undefined;
    const caseItem = cases.find((item) => item.id === debriefIntent.caseId);
    const reminder = caseItem?.debriefReminders?.find((item) => item.id === debriefIntent.reminderId);
    return caseItem && reminder ? { caseItem, reminder } : undefined;
  }, [cases, debriefIntent]);
  const activeDebriefNeedsNextStep = Boolean(
    activeDebrief &&
    !isAgendaExcludedCase(activeDebrief.caseItem) &&
    (!activeDebrief.caseItem.followUp || isCurrentFollowUpDebrief(activeDebrief.caseItem, activeDebrief.reminder))
  );

  const activeCases = cases.filter(isActiveCase);
  const liveCases = cases.filter(isLiveCase);
  const archivedCases = cases.filter(isArchivedCase);
  const nextScheduledCase = useMemo(
    () =>
      cases
        .filter(
          (caseItem) =>
            (isActiveCase(caseItem) || (isWaitingCase(caseItem) && nextActionBucket(caseItem, operationalNow) !== "Neaktívne")) &&
            !isCaseWorkflowGatedByDebrief(caseItem, operationalNow) &&
            Boolean(caseItem.followUp?.date && caseItem.followUp.time && caseItem.followUp.purpose)
        )
        .sort((a, b) =>
          `${a.followUp?.date ?? "9999-99-99"}${a.followUp?.time ?? "99:99"}`.localeCompare(
            `${b.followUp?.date ?? "9999-99-99"}${b.followUp?.time ?? "99:99"}`
          )
        )[0],
    [cases, operationalNow]
  );
  const hotCases = activeCases.filter((caseItem) => caseItem.priority === "HOT");
  const negotiationCases = activeCases.filter((caseItem) => caseItem.stage === "Rokovanie");
  const totalPotential = activeCases.reduce((sum, caseItem) => sum + caseItem.commissionPotential, 0);
  const hotPotential = hotCases.reduce((sum, caseItem) => sum + caseItem.commissionPotential, 0);
  const negotiationPotential = negotiationCases.reduce((sum, caseItem) => sum + caseItem.commissionPotential, 0);

  const todayCases = useMemo(() => {
    const visibleBuckets: string[] =
      todayFilter === "Všetko" ? ["Omeškané", "Dnes", "Nadchádzajúce", "Bez kroku"] : [todayFilter];

    return cases
      .filter((caseItem) => visibleBuckets.includes(followUpBucket(caseItem, operationalNow)))
      .sort(sortByPriorityAndTime(operationalNow));
  }, [cases, operationalNow, todayFilter]);

  const todayCounts = useMemo(
    () => ({
      all: cases.filter((caseItem) => ["Omeškané", "Dnes", "Nadchádzajúce", "Bez kroku"].includes(followUpBucket(caseItem, operationalNow))).length,
      overdue: cases.filter((caseItem) => followUpBucket(caseItem, operationalNow) === "Omeškané").length,
      today: cases.filter((caseItem) => followUpBucket(caseItem, operationalNow) === "Dnes").length,
      upcoming: cases.filter((caseItem) => followUpBucket(caseItem, operationalNow) === "Nadchádzajúce").length,
      missing: cases.filter((caseItem) => followUpBucket(caseItem, operationalNow) === "Bez kroku").length
    }),
    [cases, operationalNow]
  );

  const filteredCases = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return cases
      .filter((caseItem) => matchesCaseListFilter(caseItem, caseFilter))
      .filter((caseItem) =>
        transactionFilter === "Všetky obchody" ? true : (caseItem.transactionType ?? "Iné") === transactionFilter
      )
      .filter((caseItem) => (propertyFilter === "Všetky typy" ? true : caseItem.propertyType === propertyFilter))
      .filter((caseItem) => {
        if (!normalizedQuery) {
          return true;
        }

        return [
          caseItem.contactName,
          caseItem.phone,
          caseItem.email,
          caseItem.transactionType,
          caseItem.propertyAddress,
          caseItem.city,
          caseItem.street,
          caseItem.locality,
          caseItem.propertyType,
          caseItem.apartmentRooms,
          caseItem.status,
          caseItem.stage
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if ((a.priority === "HOT") !== (b.priority === "HOT")) {
          return a.priority === "HOT" ? -1 : 1;
        }

        if (isActiveCase(a) !== isActiveCase(b)) {
          return isActiveCase(a) ? -1 : 1;
        }

        return `${a.followUp?.date ?? "9999-99-99"}${a.followUp?.time ?? "99:99"}`.localeCompare(
          `${b.followUp?.date ?? "9999-99-99"}${b.followUp?.time ?? "99:99"}`
        );
      });
  }, [caseFilter, cases, propertyFilter, query, transactionFilter]);

  const startNewCase = () => {
    setQuery("");
    setNewCaseDraft(createEmptyDraft());
    setIsCreating(true);
    setOutcomeDraft(null);
    setScreen("cases");
  };

  const toggleCreateCase = () => {
    setIsCreating((current) => {
      if (!current) {
        setNewCaseDraft(createEmptyDraft());
      }

      return !current;
    });
  };

  const openCase = (caseId: string) => {
    setSelectedId(caseId);
    setScreen("detail");
    setOutcomeDraft(null);
    setNextStepIntentId(null);
    setWaitingResultIntentId(null);
    setDebriefIntent(null);
  };

  const openCaseForNextStep = (caseId: string) => {
    const caseItem = cases.find((item) => item.id === caseId);
    if (!caseItem || !canAddMissingNextStep(caseItem)) return;
    openCase(caseId);
    const pendingDebrief = getPendingRequiredDebrief(caseItem, new Date());
    if (pendingDebrief) {
      return;
    }
    setNextStepIntentId(caseId);
  };

  const openDebrief = (caseId: string, reminderId: string) => {
    setDebriefError(undefined);
    setDebriefIntent({ caseId, reminderId });
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== caseId ? caseItem : {
      ...caseItem,
      debriefReminders: caseItem.debriefReminders?.map((reminder) => reminder.id !== reminderId ? reminder : {
        ...reminder,
        startedAt: reminder.startedAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
    }));
  };

  const snoozeDebrief = (caseId: string, reminderId: string, minutes: number) => {
    const visibleAt = new Date(Date.now() + minutes * 60_000).toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== caseId ? caseItem : {
      ...caseItem,
      debriefReminders: caseItem.debriefReminders?.map((reminder) => reminder.id !== reminderId ? reminder : {
        ...reminder, status: "snoozed", visibleAt, updatedAt: new Date().toISOString()
      }),
      updatedAt: new Date().toISOString()
    }));
    setDebriefIntent(null);
  };

  const markDebriefNotRequired = (caseId: string, reminderId: string, reason: DebriefNotRequiredReason, note = "") => {
    const now = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== caseId
      ? caseItem
      : resolveDebriefWithoutCompletion(caseItem, reminderId, reason, note, now)));
    setDebriefIntent(null);
  };

  const debriefRequestContext = (item: DebriefItem) => ({
    caseId: item.caseItem.id,
    client: item.caseItem.contactName,
    property: item.caseItem.propertyAddress,
    eventType: item.reminder.eventType,
    referenceTimestamp: item.reminder.endAt,
    timeZone: "Europe/Bratislava" as const,
    playbook: meetingPlaybookContext(item.caseItem, item.reminder.eventType)
  });

  const saveDebriefDraft = (text: string) => {
    if (!activeDebrief) return;
    const updatedAt = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
      ...caseItem,
      debriefReminders: caseItem.debriefReminders?.map((reminder) => reminder.id !== activeDebrief.reminder.id ? reminder : {
        ...reminder, debriefText: text, inputMode: "text", updatedAt
      }),
      updatedAt
    }));
  };

  const debriefErrorMessage = (error: unknown, voice = false) => {
    if (error instanceof SupabaseRequestError) {
      if (error.category === "TRANSCRIPTION_ERROR") return "Prepis nahrávky sa nepodarilo dokončiť. Nahrávka zostala pripravená na opakovanie.";
      if (error.category === "OPENAI_RATE_LIMIT") return "AI je dočasne vyťažená. Skús analýzu zopakovať o chvíľu.";
      if (error.category === "OPENAI_QUOTA_ERROR") return "AI debrief momentálne nemá dostupnú kvótu. Tvoj vstup zostal uložený.";
      if (error.category === "OPENAI_TIMEOUT" || error.category === "OPENAI_NETWORK_ERROR") return "AI debrief sa nepodarilo spojiť so službou. Skús to znova o chvíľu.";
      if (error.category === "OPENAI_INVALID_REQUEST" || error.category === "JSON_PARSE_ERROR" || error.category === "SCHEMA_VALIDATION_ERROR" || error.category === "OPENAI_RESPONSE_ERROR") {
        return "AI vrátila neplatný výsledok. Tvoj vstup zostal uložený a môžeš použiť manuálny výsledok.";
      }
      if (error.category === "MISSING_CONFIGURATION" || error.category === "OPENAI_AUTH_ERROR" || error.category === "OPENAI_PERMISSION_ERROR" || error.category === "OPENAI_MODEL_ERROR") {
        return "AI debrief momentálne nie je dostupný. Tvoj vstup zostal uložený.";
      }
      return voice ? "Nahrávku sa nepodarilo spracovať. Môžeš to skúsiť znova." : "Analýzu sa nepodarilo dokončiť. Text zostal uložený.";
    }
    return voice ? "Nahrávku sa nepodarilo spracovať." : "Analýzu sa nepodarilo dokončiť.";
  };

  const analyzeDebrief = async (text: string) => {
    if (!activeDebrief || !session) {
      setDebriefError("AI analýza vyžaduje prihlásenie a dostupné internetové pripojenie.");
      return;
    }
    setDebriefBusy(true);
    setDebriefError(undefined);
    const requestedAt = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
      ...caseItem,
        debriefReminders: caseItem.debriefReminders?.map((reminder) => reminder.id !== activeDebrief.reminder.id ? reminder : { ...reminder, status: "analyzing", inputMode: "text", debriefText: text, analysis: undefined, analysisRequestedAt: requestedAt, updatedAt: requestedAt })
    }));
    try {
      const result = await analyzeMeetingDebrief(session, {
        ...debriefRequestContext(activeDebrief),
        notes: text
      });
      adoptSession(result.session);
      const completedAt = new Date().toISOString();
      setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
        ...caseItem,
        debriefReminders: caseItem.debriefReminders?.map((reminder) =>
          reminder.id !== activeDebrief.reminder.id || reminder.status === "completed" || reminder.status === "not_required"
            ? reminder
            : { ...reminder, status: "review", analysis: result.analysis, analysisCompletedAt: completedAt, updatedAt: completedAt })
      }));
    } catch (error) {
      setDebriefError(debriefErrorMessage(error));
      setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
        ...caseItem,
        debriefReminders: caseItem.debriefReminders?.map((reminder) =>
          reminder.id !== activeDebrief.reminder.id || reminder.status === "completed" || reminder.status === "not_required"
            ? reminder
            : { ...reminder, status: "pending", updatedAt: new Date().toISOString() })
      }));
    } finally {
      setDebriefBusy(false);
    }
  };

  const analyzeVoiceDebrief = async (audio: Blob, durationSeconds: number) => {
    if (!activeDebrief || !session) {
      setDebriefError("AI prepis vyžaduje prihlásenie a dostupné internetové pripojenie.");
      return;
    }
    setDebriefBusy(true);
    setDebriefError(undefined);
    const requestedAt = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
      ...caseItem,
      debriefReminders: caseItem.debriefReminders?.map((reminder) => reminder.id !== activeDebrief.reminder.id ? reminder : {
        ...reminder, status: "analyzing", inputMode: "voice", audioDurationSeconds: durationSeconds, analysis: undefined,
        analysisRequestedAt: requestedAt, updatedAt: requestedAt
      })
    }));
    try {
      const result = await analyzeMeetingDebriefAudio(
        session,
        debriefRequestContext(activeDebrief),
        audio,
        durationSeconds
      );
      adoptSession(result.session);
      const completedAt = new Date().toISOString();
      setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
        ...caseItem,
        debriefReminders: caseItem.debriefReminders?.map((reminder) =>
          reminder.id !== activeDebrief.reminder.id || reminder.status === "completed" || reminder.status === "not_required" ? reminder : {
          ...reminder, status: "review", inputMode: "voice", audioDurationSeconds: durationSeconds,
          debriefText: result.transcript, analysis: result.analysis, analysisCompletedAt: completedAt, updatedAt: completedAt
        })
      }));
    } catch (error) {
      setDebriefError(debriefErrorMessage(error, true));
      setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id ? caseItem : {
        ...caseItem,
        debriefReminders: caseItem.debriefReminders?.map((reminder) =>
          reminder.id !== activeDebrief.reminder.id || reminder.status === "completed" || reminder.status === "not_required" ? reminder : {
          ...reminder, status: "pending", inputMode: "voice", audioDurationSeconds: durationSeconds, updatedAt: new Date().toISOString()
        })
      }));
    } finally {
      setDebriefBusy(false);
    }
  };

  const resolveMissedMeeting = (
    caseId: string,
    reminderId: string,
    reason: DebriefNotRequiredReason,
    note: string,
    reschedule: boolean
  ) => {
    const now = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== caseId
      ? caseItem
      : resolveDebriefWithoutCompletion(
          caseItem,
          reminderId,
          reason,
          note,
          now,
          reschedule ? "Stretnutie sa neuskutočnilo · nový termín" : "Stretnutie sa neuskutočnilo"
        )));
    setDebriefIntent(null);
    if (reschedule) {
      setSelectedId(caseId);
      setScreen("detail");
      setNextStepIntentId(caseId);
    }
  };

  const confirmDebrief = (analysis: DebriefAnalysis, text: string, shotScoreReview: DebriefShotScoreReview, approvedCrmFacts: DebriefCrmFact[], nextStep?: NextStepDraft) => {
    if (!activeDebrief) return;
    if (activeDebriefNeedsNextStep && !nextStep) return;
    const now = new Date().toISOString();
    const debriefId = createId("debrief");
    const reviewedShotScore = shotScoreValue(shotScoreReview);
    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== activeDebrief.caseItem.id) return caseItem;
      const currentReminder = caseItem.debriefReminders?.find((reminder) => reminder.id === activeDebrief.reminder.id);
      if (currentReminder?.status === "completed" || caseItem.meetingDebriefs?.some((debrief) => debrief.reminderId === activeDebrief.reminder.id)) {
        return caseItem;
      }
      const closesCurrentMeeting = isCurrentFollowUpDebrief(caseItem, activeDebrief.reminder);
      const completion = closesCurrentMeeting && nextStep
        ? completeCaseNextActionWithManualNext(caseItem, nextStep, now)
        : null;
      const scheduledFollowUp: FollowUp | undefined = !caseItem.followUp && nextStep
        ? { date: nextStep.date, time: nextStep.time, ...(nextStep.endTime ? { endTime: nextStep.endTime } : {}), purpose: nextStep.purpose.trim() }
        : undefined;
      let updatedCase = completion?.caseItem ?? (scheduledFollowUp ? {
        ...caseItem,
        followUp: scheduledFollowUp,
        nextStep: nextStepLabel(scheduledFollowUp),
        timeline: [createActivity({
          type: "system",
          title: "Ďalší krok naplánovaný",
          body: nextStepLabel(scheduledFollowUp),
          followUp: scheduledFollowUp
        }, now), ...caseItem.timeline]
      } : caseItem);
      if (completion && followUpActivityType(caseItem) === "viewing") {
        const hasFirstViewing = caseItem.marketTimeline?.events.some((event) => event.type === "FIRST_VIEWING") ?? false;
        updatedCase = {
          ...updatedCase,
          marketTimeline: milestoneMarketTimeline(caseItem, hasFirstViewing ? "VIEWING" : "FIRST_VIEWING", now)
        };
      }
      const shotScoreActivityId = completion?.activityId ?? activeDebrief.reminder.activityId ?? activeDebrief.reminder.eventId;
      const existingShotScore = caseItem.shotScores?.find((score) =>
        score.activityId === shotScoreActivityId
      );
      const shotScoreId = existingShotScore?.id ?? createId("shot-score");
      return {
        ...updatedCase,
        debriefReminders: updatedCase.debriefReminders?.map((reminder) => reminder.id !== activeDebrief.reminder.id ? reminder : { ...reminder, status: "completed", debriefId, activityId: shotScoreActivityId, debriefText: text, analysis, completedAt: now, updatedAt: now }),
        meetingDebriefs: [{
          id: debriefId, reminderId: activeDebrief.reminder.id, eventId: activeDebrief.reminder.eventId,
          caseId: caseItem.id, activityId: shotScoreActivityId, inputText: text,
          inputMode: currentReminder?.inputMode ?? "text", audioDurationSeconds: currentReminder?.audioDurationSeconds,
          analysis, approvedCrmFacts, confirmedNextAction: nextStep
            ? { date: nextStep.date, time: nextStep.time, ...(nextStep.endTime ? { endTime: nextStep.endTime } : {}), purpose: nextStep.purpose.trim() }
            : undefined,
          shotScoreId, createdAt: now, confirmedAt: now
        }, ...(caseItem.meetingDebriefs ?? [])],
        shotScores: existingShotScore ? caseItem.shotScores?.map((score) => score.id !== existingShotScore.id ? score : {
          ...score,
          ...shotScoreReview,
          score: reviewedShotScore,
          completedAt: now
        }) : [{
          id: shotScoreId,
          caseId: caseItem.id,
          activityId: shotScoreActivityId,
          activityType: activeDebrief.reminder.eventType === "property_viewing" ? "viewing" : "meeting",
          ...shotScoreReview,
          score: reviewedShotScore,
          completedAt: now
        }, ...(caseItem.shotScores ?? [])],
        timeline: [createActivity({ type: "debrief", title: "AI debrief dokončený", body: `${analysis.summary} Shot Score: ${reviewedShotScore} %. Ďalší krok: ${nextStep ? nextStepLabel(nextStep) : updatedCase.followUp ? nextStepLabel(updatedCase.followUp) : analysis.nextStep}`, shotScoreId }, now), ...updatedCase.timeline],
        updatedAt: now
      };
    }));
    setDebriefIntent(null);
    setDebriefConfirmation(
      `Debrief hotový. Shot Score: ${reviewedShotScore} %. Ďalší krok: ${nextStep ? nextStepLabel(nextStep) : activeDebrief.caseItem.followUp ? nextStepLabel(activeDebrief.caseItem.followUp) : analysis.nextStep}`
    );
    window.setTimeout(() => setDebriefConfirmation(undefined), 5_000);
  };

  const confirmManualMeetingOutcome = (outcome: MeetingOutcome, nextStep?: NextStepDraft) => {
    if (!activeDebrief) return;
    const now = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => caseItem.id !== activeDebrief.caseItem.id
      ? caseItem
      : applyManualMeetingOutcome(caseItem, activeDebrief.reminder.id, outcome, nextStep, now)));
    setDebriefIntent(null);
    setDebriefConfirmation(outcome === "Nezáujem / uzavrieť prípad"
      ? "Výsledok stretnutia uložený. Prípad bol presunutý do archívu."
      : `Výsledok stretnutia uložený. Ďalší krok: ${nextStep ? nextStepLabel(nextStep) : "bez ďalšieho kroku"}`);
    window.setTimeout(() => setDebriefConfirmation(undefined), 5_000);
  };

  const openWaitingResult = (caseId: string) => {
    const caseItem = cases.find((item) => item.id === caseId);
    if (!caseItem || !isWaitingCase(caseItem)) return;
    openCase(caseId);
    if (getPendingRequiredDebrief(caseItem, new Date())) return;
    setWaitingResultIntentId(caseId);
  };

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    const caseId = parameters.get("case")?.trim();
    if (!caseId || !cases.some((caseItem) => caseItem.id === caseId)) {
      return;
    }

    openCase(caseId);
    const requestedAction = parameters.get("action");
    if (requestedAction === "next-step") {
      const caseItem = cases.find((item) => item.id === caseId);
      const pendingDebrief = caseItem ? getPendingRequiredDebrief(caseItem, new Date()) : undefined;
      if (!pendingDebrief) setNextStepIntentId(caseId);
    } else if (requestedAction === "debrief") {
      // The case route owns the mandatory entry gate. Opening the editor is always an explicit user action.
      setDebriefIntent(null);
    } else if (requestedAction === "call" && cases.some((caseItem) => caseItem.id === caseId && caseItem.phone.trim())) {
      setCallPromptCaseId(caseId);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("case");
    url.searchParams.delete("action");
    url.searchParams.delete("reminder");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }, [cases]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const handleServiceWorkerMessage = (event: MessageEvent<{ type?: string; caseId?: string; activityType?: string }>) => {
      const caseId = event.data?.type === "FISPRO_OPEN_CASE" ? event.data.caseId?.trim() : undefined;
      if (!caseId) {
        return;
      }

      if (cases.some((caseItem) => caseItem.id === caseId)) {
        const caseItem = cases.find((item) => item.id === caseId);
        const requiredDebrief = caseItem ? getPendingRequiredDebrief(caseItem, new Date()) : undefined;
        openCase(caseId);
        if (!requiredDebrief && event.data?.activityType !== "Debrief") {
          setNextStepIntentId(caseId);
        }
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set("case", caseId);
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    };

    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
  }, [cases]);

  const logoutWithPushCleanup = async () => {
    try {
      await disconnectSubscription();
    } catch {
      // The browser subscription is invalidated in a finally block; logout must still continue.
    } finally {
      await logout();
    }
  };

  const createCase = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const followUp: FollowUp = {
      date: newCaseDraft.followUpDate,
      time: newCaseDraft.followUpTime,
      purpose: newCaseDraft.followUpPurpose.trim()
    };

    if (!followUp.date || !followUp.time || !followUp.purpose) {
      return;
    }

    const askingPrice = Number(newCaseDraft.askingPrice) || 0;
    const recommendedPrice = Number(newCaseDraft.recommendedPrice) || 0;
    const commissionPotential =
      Number(newCaseDraft.commissionPotential) || Math.round(recommendedPrice * 0.03);
    const city = newCaseDraft.city.trim();
    const street = newCaseDraft.street.trim();
    const locality = newCaseDraft.locality.trim();
    const propertyAddress = newCaseDraft.propertyAddress.trim() || [street, city].filter(Boolean).join(", ");
    const area = Number(newCaseDraft.area);
    const now = new Date().toISOString();

    const createdCase: RealEstateCase = {
      id: createId("case"),
      contactName: newCaseDraft.contactName.trim(),
      phone: newCaseDraft.phone.trim(),
      email: newCaseDraft.email.trim(),
      transactionType: newCaseDraft.transactionType,
      propertyAddress,
      city: city || undefined,
      cityNormalized: city ? newCaseDraft.cityNormalized || normalizeLocationText(city) : undefined,
      citySource: city ? newCaseDraft.citySource : undefined,
      municipalityId: city ? newCaseDraft.municipalityId : undefined,
      street: street || undefined,
      streetNormalized: street ? newCaseDraft.streetNormalized || normalizeLocationText(street) : undefined,
      streetSource: street ? newCaseDraft.streetSource : undefined,
      streetId: street ? newCaseDraft.streetId : undefined,
      locality: locality || undefined,
      propertyType: newCaseDraft.propertyType,
      apartmentRooms: newCaseDraft.propertyType === "Byt" ? newCaseDraft.apartmentRooms : undefined,
      cooperationType: newCaseDraft.cooperationType,
      area: Number.isFinite(area) && area > 0 ? area : undefined,
      askingPrice,
      recommendedPrice,
      commissionPotential,
      status: "Prvý cold call",
      stage: "Prvý cold call",
      priority: "Bežný",
      nextStep: nextStepLabel(followUp, "Prvý cold call"),
      followUp,
      notes: newCaseDraft.notes.trim(),
      timeline: [
        createActivity(
          {
            type: "system",
            title: "Prípad založený",
            body: `Workflow začína krokom Prvý cold call. Kontext: ${caseContextLabel({
              transactionType: newCaseDraft.transactionType,
              propertyType: newCaseDraft.propertyType,
              apartmentRooms: newCaseDraft.propertyType === "Byt" ? newCaseDraft.apartmentRooms : undefined
            })}. Follow-up: ${nextStepLabel(followUp)}.`,
            followUp
          },
          now
        )
      ],
      createdAt: now,
      updatedAt: now
    };

    setCases((currentCases) => [createdCase, ...currentCases]);
    setSelectedId(createdCase.id);
    setNewCaseDraft(createEmptyDraft());
    setIsCreating(false);
    setScreen("detail");
  };

  const updateNotes = (caseId: string, notes: string) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) =>
        caseItem.id === caseId ? { ...caseItem, notes, updatedAt: new Date().toISOString() } : caseItem
      )
    );
  };

  const updateCase = (caseId: string, draft: CaseEditDraft) => {
    const city = draft.city.trim();
    const street = draft.street.trim();
    const locality = draft.locality.trim();
    const area = Number(draft.area);

    const askingPrice = Number(draft.askingPrice) || 0;
    const recommendedPrice = Number(draft.recommendedPrice) || 0;
    const explicitCommission = Number(draft.commissionPotential) || 0;

    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId) return caseItem;
        const now = new Date().toISOString();
        const marketTimeline = priceChangedMarketTimeline(caseItem, caseItem.askingPrice, askingPrice, now);
        return {
          ...caseItem,
          contactName: draft.contactName.trim(),
          phone: draft.phone.trim(),
          email: draft.email.trim(),
          transactionType: draft.transactionType,
          propertyAddress: caseItem.propertyAddress || [street, city].filter(Boolean).join(", "),
          city: city || undefined,
          cityNormalized: city ? draft.cityNormalized || normalizeLocationText(city) : undefined,
          citySource: city ? draft.citySource : undefined,
          municipalityId: city ? draft.municipalityId : undefined,
          street: street || undefined,
          streetNormalized: street ? draft.streetNormalized || normalizeLocationText(street) : undefined,
          streetSource: street ? draft.streetSource : undefined,
          streetId: street ? draft.streetId : undefined,
          locality: locality || undefined,
          area: Number.isFinite(area) && area > 0 ? area : undefined,
          propertyType: draft.propertyType,
          apartmentRooms: draft.propertyType === "Byt" ? draft.apartmentRooms : undefined,
          cooperationType: draft.cooperationType,
          askingPrice,
          marketTimeline,
          recommendedPrice,
          commissionPotential: explicitCommission || Math.round(recommendedPrice * 0.03),
          notes: draft.notes.trim(),
          timeline: [
            createActivity({
              type: "note",
              title: "Prípad upravený",
              body: "Aktualizované kontaktné, realitné alebo cenové údaje prípadu."
            }, now),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const beginOutcomeForCase = (caseItem: RealEstateCase, outcome: CallOutcome) => {
    if (isCaseWorkflowGatedByDebrief(caseItem)) return;
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    const preferredTime = caseItem.followUp?.time ?? nextHour.toTimeString().slice(0, 5);
    const ownerSaleFirstStep = getOwnerSaleSequenceStep(1);
    const ownerSaleFollowUp = ownerSaleFirstStep
      ? createOwnerSaleFollowUpDraft(ownerSaleFirstStep, preferredTime)
      : undefined;

    setOutcomeDraft({
      outcome,
      date: outcome === "Predáva sám" && ownerSaleFollowUp ? ownerSaleFollowUp.date : toInputDate(nextHour),
      time: outcome === "Predáva sám" && ownerSaleFollowUp ? ownerSaleFollowUp.time : preferredTime,
      purpose: outcome === "Predáva sám" && ownerSaleFollowUp ? ownerSaleFollowUp.purpose : outcomeConfig[outcome].defaultPurpose
    });
  };

  const beginOutcome = (outcome: CallOutcome) => {
    if (selectedCase) {
      beginOutcomeForCase(selectedCase, outcome);
    }
  };

  const beginNowOutcome = (caseId: string, outcome: CallOutcome) => {
    const caseItem = cases.find((currentCase) => currentCase.id === caseId);

    if (!caseItem) {
      return;
    }

    setSelectedId(caseId);
    beginOutcomeForCase(caseItem, outcome);
    setScreen("detail");
  };

  const saveOutcome = () => {
    if (!selectedCase || !outcomeDraft || isCaseWorkflowGatedByDebrief(selectedCase)) {
      return;
    }

    const config = outcomeConfig[outcomeDraft.outcome];
    const followUp =
      config.requiresFollowUp && outcomeDraft.date && outcomeDraft.time && outcomeDraft.purpose.trim()
        ? {
            date: outcomeDraft.date,
            time: outcomeDraft.time,
            purpose: outcomeDraft.purpose.trim(),
            ...(isDebriefPurpose(outcomeDraft.purpose) && outcomeDraft.endTime ? { endTime: outcomeDraft.endTime } : {})
          }
        : undefined;

    if (config.requiresFollowUp && !followUp) {
      return;
    }

    const now = new Date().toISOString();

    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== selectedCase.id) {
          return caseItem;
        }

        const ownerSaleStep = outcomeDraft.outcome === "Predáva sám" ? getOwnerSaleSequenceStep(1) : undefined;
        const listingStop = config.stage !== "Zainzerované"
          ? stopListingWorkflow(caseItem, now, `Výsledok hovoru: ${outcomeDraft.outcome}`)
          : null;
        const endedOwnerSaleSequence = config.stage === "Odmietnutý" && isOwnerSaleSequenceActive(caseItem)
          ? createEndedOwnerSaleSequence(caseItem, "Nechce ďalší kontakt", now)
          : caseItem.ownerSaleSequence;
        const marketTimeline = caseItem.listingStartedAt
          ? milestoneMarketTimeline(caseItem, "FIRST_CALL", now)
          : caseItem.marketTimeline;
        const body = ownerSaleStep && followUp
          ? `Majiteľ predáva sám. Spustená sekvencia: ${ownerSaleStep.title}. Naplánované: ${nextStepLabel(
              followUp
            )}. Cieľ: ${ownerSaleStep.goal} Princíp: netlačiť na klienta.`
          : followUp
            ? `Naplánovaný follow-up: ${nextStepLabel(followUp)}.`
            : "Prípad je označený ako nezáujem bez ďalšieho follow-upu.";

        return {
          ...caseItem,
          status: config.status,
          stage: config.stage,
          ownerSaleSequence:
            outcomeDraft.outcome === "Predáva sám"
              ? {
                  status: "Aktívna",
                  currentStep: 1,
                  completedContacts: 0
                }
              : endedOwnerSaleSequence,
          listingFollowUpSequence: listingStop?.sequence ?? caseItem.listingFollowUpSequence,
          marketTimeline,
          nextStep: followUp ? nextStepLabel(followUp) : config.nextStep,
          followUp,
          timeline: [
            createActivity(
              {
                type: outcomeDraft.outcome === "Predáva sám" ? "owner-sale" : "call",
                title: `Výsledok hovoru: ${outcomeDraft.outcome}`,
                body,
                outcome: outcomeDraft.outcome,
                followUp
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );

    setOutcomeDraft(null);
  };

  const updateStage = (caseId: string, stage: CaseStage, reservationUntil?: string) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (
          caseItem.id !== caseId ||
          (caseItem.stage === stage && !(stage === "Rezervované" && reservationUntil && !isReservationControl(caseItem))) ||
          isCaseWorkflowGatedByDebrief(caseItem) ||
          (stage === "Zainzerované" && !canStartListingWorkflow(caseItem)) ||
          (stage === "Rezervované" && !reservationUntil)
        ) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const listingStart = stage === "Zainzerované" ? startListingWorkflow(caseItem, now) : null;
        const listingStop = stage !== "Zainzerované"
          ? stopListingWorkflow(caseItem, now, `Fáza zmenená na ${stage}`)
          : null;
        const leavesReservation = caseItem.stage === "Rezervované" && stage !== "Rezervované";
        const clearsWorkflow = terminalStages.includes(stage) || stage === "Odmietnutý" || listingStop?.clearFollowUp || leavesReservation;
        const resultingFollowUp = listingStart?.followUp ?? (clearsWorkflow ? undefined : caseItem.followUp);
        const nextStep = terminalStages.includes(stage) || stage === "Odmietnutý"
          ? "Bez ďalšieho kroku"
          : resultingFollowUp
            ? nextStepLabel(resultingFollowUp)
            : "Chýba ďalší krok";
        const ownerSaleSequence =
          (terminalStages.includes(stage) || stage === "Odmietnutý") && isOwnerSaleSequenceActive(caseItem)
            ? createEndedOwnerSaleSequence(caseItem, "Sekvencia ukončená manuálne", now)
            : caseItem.ownerSaleSequence;
        const listingCase = listingStart ? {
          ...caseItem,
          listingStartedAt: listingStart.listingStartedAt,
          initialListingPrice: listingStart.initialListingPrice,
          marketTimeline: caseItem.marketTimeline
        } : caseItem;
        let marketTimeline = listingStart?.startedNow
          ? listedMarketTimeline(listingCase, now)
          : caseItem.marketTimeline;
        if (stage === "Rezervované") {
          marketTimeline = milestoneMarketTimeline({ ...caseItem, marketTimeline }, "RESERVED", now);
          return startReservationWorkflow({
            ...caseItem,
            ownerSaleSequence,
            listingFollowUpSequence: listingStop?.sequence ?? caseItem.listingFollowUpSequence,
            marketTimeline
          }, reservationUntil!, now);
        }

        return {
          ...caseItem,
          stage,
          status: stage === "Stratené" ? "Odmietnutý" : (stage as RealEstateCase["status"]),
          reservationUntil: leavesReservation ? undefined : caseItem.reservationUntil,
          followUp: resultingFollowUp,
          ownerSaleSequence,
          listingStartedAt: listingStart?.listingStartedAt ?? caseItem.listingStartedAt,
          initialListingPrice: listingStart?.initialListingPrice ?? caseItem.initialListingPrice,
          listingFollowUpSequence: listingStart?.listingFollowUpSequence ?? listingStop?.sequence ?? caseItem.listingFollowUpSequence,
          marketTimeline,
          nextStep,
          timeline: [
            createActivity(
              {
                type: terminalStages.includes(stage) ? "closed" : "stage",
                title: listingStart?.startedNow ? "Nehnuteľnosť zainzerovaná" : "Zmena fázy",
                body: listingStart?.startedNow
                  ? `Inzercia spustená. ${listingStart.followUp ? `Automatický ďalší krok: ${nextStepLabel(listingStart.followUp)}.` : ""}`.trim()
                  : `Fáza zmenená z ${caseItem.stage} na ${stage}.`
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const resolveReservation = (
    caseId: string,
    outcome: ReservationOutcome,
    reservationUntil?: string,
    nextStep?: NextStepDraft
  ) => {
    const now = new Date().toISOString();
    const nextFollowUp: FollowUp | undefined = nextStep?.date && nextStep.time && nextStep.purpose.trim()
      ? { date: nextStep.date, time: nextStep.time, purpose: nextStep.purpose.trim() }
      : undefined;

    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || !isReservationControl(caseItem)) return caseItem;
      if (outcome === "extended") {
        return reservationUntil ? extendReservationWorkflow(caseItem, reservationUntil, now) : caseItem;
      }
      return resolveReservationWorkflow(caseItem, outcome, now, nextFollowUp);
    }));

    if ((outcome === "fee_returned" || outcome === "fee_settled") && !nextFollowUp) {
      setNextStepIntentId(caseId);
    }
  };

  const updateFollowUp = (caseId: string, draft: NextStepDraft) => {
    const followUp = {
      date: draft.date,
      time: draft.time,
      purpose: draft.purpose.trim(),
      ...(isDebriefPurpose(draft.purpose) && draft.endTime ? { endTime: draft.endTime } : {})
    };

    if (!followUp.date || !followUp.time || !followUp.purpose) {
      return;
    }

    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || isCaseWorkflowGatedByDebrief(caseItem)) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const updatedFollowUp: FollowUp = { ...caseItem.followUp, ...followUp };
        if (!isDebriefPurpose(followUp.purpose)) delete updatedFollowUp.endTime;
        const ownerSaleStep = isOwnerSaleSequenceActive(caseItem)
          ? getCurrentOwnerSaleSequenceStep(caseItem)
          : undefined;

        return {
          ...caseItem,
          followUp: updatedFollowUp,
          reservationUntil: isReservationControl(caseItem) ? updatedFollowUp.date : caseItem.reservationUntil,
          nextStep: nextStepLabel(updatedFollowUp),
          timeline: [
            createActivity(
              {
                type: ownerSaleStep ? "owner-sale" : "follow-up",
                title: ownerSaleStep
                  ? `${ownerSaleStep.step}. follow-up sekvencie naplánovaný`
                  : caseItem.followUp
                    ? "Ďalší krok preložený"
                    : "Ďalší krok doplnený",
                body: ownerSaleStep
                  ? `Sekvencia Predáva sám: ${nextStepLabel(followUp)}. Cieľ: ${ownerSaleStep.goal} Princíp: netlačiť na klienta.`
                  : `Nový termín: ${nextStepLabel(updatedFollowUp)}.`,
                followUp: updatedFollowUp
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const completeFollowUp = (caseId: string): FollowUpCompletion | null => {
    const caseSnapshot = cases.find((caseItem) => caseItem.id === caseId && caseItem.followUp);
    if (!caseSnapshot?.followUp || isCaseWorkflowGatedByDebrief(caseSnapshot)) return null;

    const now = new Date().toISOString();
    const activityType = followUpActivityType(caseSnapshot);
    const completion = completeCaseNextAction(caseSnapshot, now);
    if (!completion) return null;

    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || !caseItem.followUp) {
          return caseItem;
        }

        const completedActivityType = followUpActivityType(caseItem);
        const hasFirstViewing = caseItem.marketTimeline?.events.some((event) => event.type === "FIRST_VIEWING") ?? false;
        const marketTimeline = completedActivityType === "viewing"
          ? milestoneMarketTimeline(caseItem, hasFirstViewing ? "VIEWING" : "FIRST_VIEWING", now)
          : caseItem.marketTimeline;
        return activateCompletedEventDebrief({
          ...completion.caseItem,
          marketTimeline,
        }, caseItem.followUp, completion.activityId, new Date(now));
      })
    );

    return { activityId: completion.activityId, activityType, completedAt: now };
  };

  const completeFollowUpWithNext = (caseId: string, draft: NextStepDraft): FollowUpCompletion | null => {
    const caseSnapshot = cases.find((caseItem) => caseItem.id === caseId && caseItem.followUp);
    if (!caseSnapshot?.followUp || isCaseWorkflowGatedByDebrief(caseSnapshot)) return null;

    const now = new Date().toISOString();
    const activityType = followUpActivityType(caseSnapshot);
    const completion = completeCaseNextActionWithManualNext(caseSnapshot, draft, now);
    if (!completion) return null;

    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || !caseItem.followUp) return caseItem;
      const completedActivityType = followUpActivityType(caseItem);
      const hasFirstViewing = caseItem.marketTimeline?.events.some((event) => event.type === "FIRST_VIEWING") ?? false;
      const marketTimeline = completedActivityType === "viewing"
        ? milestoneMarketTimeline(caseItem, hasFirstViewing ? "VIEWING" : "FIRST_VIEWING", now)
        : caseItem.marketTimeline;
      return activateCompletedEventDebrief(
        { ...completion.caseItem, marketTimeline },
        caseItem.followUp,
        completion.activityId,
        new Date(now)
      );
    }));

    return {
      activityId: completion.activityId,
      activityType,
      completedAt: now
    };
  };

  const completeOwnerSaleContact = (caseId: string, draft: OwnerSaleContactDraft) => {
    const result = draft.result.trim();
    const note = draft.note.trim();

    if (!result || !note) {
      return;
    }

    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || !isOwnerSaleSequenceActive(caseItem) || isCaseWorkflowGatedByDebrief(caseItem)) {
          return caseItem;
        }

        const currentStep = getCurrentOwnerSaleSequenceStep(caseItem);
        if (!currentStep) {
          return caseItem;
        }

        const nextStep = getNextOwnerSaleSequenceStep(currentStep.step);
        const nextFollowUp =
          nextStep && draft.nextDate && draft.nextTime && draft.nextPurpose.trim()
            ? {
                date: draft.nextDate,
                time: draft.nextTime,
                purpose: draft.nextPurpose.trim()
              }
            : undefined;

        if (nextStep && !nextFollowUp) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const completedContacts = Math.max(caseItem.ownerSaleSequence?.completedContacts ?? 0, currentStep.step);
        const endReason = nextStep ? undefined : draft.endReason;
        const finalState = endReason ? ownerSaleEndState[endReason] : undefined;

        return {
          ...caseItem,
          status: nextStep ? "Predáva sám" : finalState?.status ?? caseItem.status,
          stage: nextStep ? "Follow-up" : finalState?.stage ?? caseItem.stage,
          leadState: finalState?.stage === "Vyradený" ? "Vyradený" : caseItem.leadState,
          excludedAt: finalState?.stage === "Vyradený" ? now : caseItem.excludedAt,
          exclusionReason: finalState?.stage === "Vyradený" ? endReason : caseItem.exclusionReason,
          lostReason: finalState?.stage === "Vyradený" ? endReason : caseItem.lostReason,
          closedLostAt: finalState?.stage === "Vyradený" ? now : caseItem.closedLostAt,
          lastBusinessStage: finalState?.stage === "Vyradený" ? caseItem.stage : caseItem.lastBusinessStage,
          followUp: nextFollowUp,
          nextStep: nextFollowUp ? nextStepLabel(nextFollowUp) : `Sekvencia ukončená: ${endReason}.`,
          ownerSaleSequence: {
            status: nextStep ? "Aktívna" : "Ukončená",
            currentStep: nextStep?.step ?? currentStep.step,
            completedContacts,
            endReason,
            endedAt: nextStep ? undefined : now,
            lastResult: result,
            lastNote: note
          },
          timeline: [
            createActivity(
              {
                type: "owner-sale",
                title: `${currentStep.step}. follow-up - majiteľ predáva sám`,
                body: buildOwnerSaleTimelineBody(currentStep.step, result, note, nextFollowUp, endReason),
                followUp: nextFollowUp
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const endOwnerSaleSequence = (caseId: string, draft: OwnerSaleEndDraft) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || !caseItem.ownerSaleSequence || isCaseWorkflowGatedByDebrief(caseItem)) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const note = draft.note.trim();
        const endState = ownerSaleEndState[draft.reason];

        return {
          ...caseItem,
          stage: endState.stage,
          status: endState.status,
          leadState: endState.stage === "Vyradený" ? "Vyradený" : "Aktívny",
          excludedAt: endState.stage === "Vyradený" ? now : undefined,
          exclusionReason: endState.stage === "Vyradený" ? draft.reason : undefined,
          lostReason: endState.stage === "Vyradený" ? draft.reason : caseItem.lostReason,
          closedLostAt: endState.stage === "Vyradený" ? now : undefined,
          lastBusinessStage: endState.stage === "Vyradený" ? caseItem.stage : caseItem.lastBusinessStage,
          followUp: undefined,
          nextStep: endState.nextStep,
          ownerSaleSequence: {
            ...caseItem.ownerSaleSequence,
            status: "Ukončená",
            currentStep: Math.max(1, Math.min(4, caseItem.ownerSaleSequence.currentStep || 1)),
            completedContacts: Math.max(0, Math.min(4, caseItem.ownerSaleSequence.completedContacts || 0)),
            endReason: draft.reason,
            endedAt: now,
            lastNote: note || caseItem.ownerSaleSequence.lastNote
          },
          timeline: [
            createActivity(
              {
                type: "owner-sale",
                title: "Sekvencia Predáva sám ukončená",
                body: `Dôvod: ${draft.reason}.${note ? ` Poznámka: ${note}` : ""}`
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const togglePriority = (caseId: string) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const priority = caseItem.priority === "HOT" ? "Bežný" : "HOT";

        return {
          ...caseItem,
          priority,
          timeline: [
            createActivity(
              {
                type: "priority",
                title: "Zmena priority",
                body: priority === "HOT" ? "Prípad označený ako HOT." : "Prípad vrátený na bežnú prioritu."
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const closeCase = (caseId: string, draft: ClosingDraft) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || isCaseWorkflowGatedByDebrief(caseItem)) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const closedAt = draft.closedAt || today;
        const actualCommission = Number(draft.actualCommission) || caseItem.actualCommission || 0;
        const closingNote = draft.note.trim();
        const ownerSaleSequence = isOwnerSaleSequenceActive(caseItem)
          ? createEndedOwnerSaleSequence(caseItem, "Získaný nábor", now)
          : caseItem.ownerSaleSequence;

        return {
          ...caseItem,
          stage: "Uzavretý",
          status: "Uzavretý",
          followUp: undefined,
          nextStep: "Bez ďalšieho kroku",
          ownerSaleSequence,
          closedAt,
          actualCommission,
          closingNote,
          timeline: [
            createActivity(
              {
                type: "closed",
                title: "Prípad uzavretý",
                body: `Uzavreté ${closedAt}. Skutočná provízia: ${actualCommission} €.${closingNote ? ` Poznámka: ${closingNote}` : ""}`
              },
              now
            ),
            ...caseItem.timeline
          ],
          updatedAt: now
        };
      })
    );
  };

  const sellCase = (caseId: string, draft: SoldDraft) => {
    const realizedPrice = Number(draft.realizedPrice);
    if (!Number.isFinite(realizedPrice) || realizedPrice <= 0 || !draft.soldAt) return;

    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || isCaseWorkflowGatedByDebrief(caseItem)) return caseItem;
      const now = new Date().toISOString();
      const ownerSaleSequence = isOwnerSaleSequenceActive(caseItem)
        ? createEndedOwnerSaleSequence(caseItem, "Nehnuteľnosť predaná", now)
        : caseItem.ownerSaleSequence;
      const marketTimeline = milestoneMarketTimeline(caseItem, "SOLD", draft.soldAt, { realizedPrice });
      const completedReservation = isReservationControl(caseItem);
      const soldCase = completedReservation
        ? completeReservationDealWorkflow(caseItem, draft.soldAt, realizedPrice, now)
        : caseItem;
      return {
        ...soldCase,
        stage: "Predané",
        status: "Predané",
        realizedPrice,
        soldAt: draft.soldAt,
        reservationUntil: undefined,
        ownerSaleSequence,
        marketTimeline,
        followUp: undefined,
        nextStep: "Bez ďalšieho kroku",
        timeline: completedReservation ? soldCase.timeline : [
          createActivity({
            type: "closed",
            title: "Nehnuteľnosť predaná",
            body: `Realizovaná cena: ${realizedPrice.toLocaleString("sk-SK")} €. Dátum predaja: ${draft.soldAt}.`
          }, now),
          ...caseItem.timeline
        ],
        updatedAt: now
      };
    }));
  };

  const saveMarketStats = (caseId: string, input: MarketStatsInput) => {
    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || !caseItem.listingStartedAt) return caseItem;
      const result = addMarketStatsSnapshot(caseItem, input);
      if (!result.created) return caseItem;
      return { ...caseItem, marketTimeline: result.state, updatedAt: new Date().toISOString() };
    }));
  };

  const rejectCase = (caseId: string, draft: RejectionDraft) => {
    setCases((currentCases) =>
      currentCases.map((caseItem) => {
        if (caseItem.id !== caseId || isCaseWorkflowGatedByDebrief(caseItem)) {
          return caseItem;
        }

        const now = new Date().toISOString();
        const lostReason = (draft.reason.trim() || "Bez uvedeného dôvodu").replace(/[.!?]+$/, "");
        const ownerSaleSequence = isOwnerSaleSequenceActive(caseItem)
          ? createEndedOwnerSaleSequence(caseItem, "Nechce ďalší kontakt", now)
          : caseItem.ownerSaleSequence;

        const listingStop = stopListingWorkflow(caseItem, now, "Prípad uzavretý ako stratený");
        return {
          ...excludeCase(caseItem, lostReason, now),
          ownerSaleSequence,
          listingFollowUpSequence: listingStop.sequence,
          closedAt: undefined,
          actualCommission: undefined,
          closingNote: undefined
        };
      })
    );
    setOutcomeDraft(null);
  };

  const waitAfterRejection = (caseId: string) => {
    const now = new Date().toISOString();
    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || isCaseWorkflowGatedByDebrief(caseItem)) return caseItem;
      const waitingCase = moveCaseToWaiting(caseItem, now, 30);
      const listingStop = stopListingWorkflow(caseItem, now, "Klient presunutý medzi čakajúce");
      return {
        ...waitingCase,
        listingFollowUpSequence: listingStop.sequence,
        ownerSaleSequence: isOwnerSaleSequenceActive(caseItem)
          ? createEndedOwnerSaleSequence(caseItem, "Nechce ďalší kontakt", now)
          : caseItem.ownerSaleSequence
      };
    }));
    setOutcomeDraft(null);
  };

  const saveWaitingOutcome = (
    caseId: string,
    outcome: Exclude<WaitingOutcome, "Definitívne vyradiť">,
    draft: NextStepDraft
  ) => {
    if (!draft.date || !draft.time || !draft.purpose.trim()) return;
    const now = new Date().toISOString();
    const followUp: FollowUp = {
      date: draft.date,
      time: draft.time,
      purpose: draft.purpose.trim(),
      ...(isDebriefPurpose(draft.purpose) && draft.endTime ? { endTime: draft.endTime } : {})
    };
    setCases((currentCases) => currentCases.map((caseItem) =>
      caseItem.id === caseId && !isCaseWorkflowGatedByDebrief(caseItem)
        ? applyWaitingOutcome(caseItem, outcome, now, followUp)
        : caseItem
    ));
  };

  const deleteCase = (caseId: string) => {
    queueDeletion(caseId);
    setCases((currentCases) => currentCases.filter((caseItem) => caseItem.id !== caseId));
    setSelectedId((current) => current === caseId ? null : current);
    setOutcomeDraft(null);
    setScreen("cases");
  };

  const restoreCase = (caseId: string) => {
    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || !isExcludedCase(caseItem)) return caseItem;
      const now = new Date().toISOString();
      const restored = restoreClosedLostCase(caseItem, now);
      return {
        ...restored,
        timeline: [
          createActivity({
            type: "stage",
            title: "Prípad obnovený",
            body: "Prípad bol obnovený do aktívneho workflow. História zostala zachovaná."
          }, now),
          ...caseItem.timeline
        ]
      };
    }));
  };

  const saveShotScore = (caseId: string, draft: ShotScoreDraft) => {
    setCases((currentCases) => currentCases.map((caseItem) => {
      if (caseItem.id !== caseId || caseItem.shotScores?.some((score) => score.activityId === draft.activityId)) {
        return caseItem;
      }

      const score = shotScoreValue(draft);
      const shotScoreId = createId("shot-score");
      const now = new Date().toISOString();
      const shotScore = { ...draft, id: shotScoreId, caseId, score, completedAt: draft.completedAt || now };
      const breakdown = [
        draft.identity ? "Identita" : null,
        draft.value ? "Hodnota" : null,
        draft.acquisition ? "Náber" : null,
        draft.nextStep ? "Next step" : null
      ].filter(Boolean).join(", ") || "Bez označenej oblasti";

      return {
        ...caseItem,
        shotScores: [shotScore, ...(caseItem.shotScores ?? [])],
        timeline: [
          createActivity({
            type: "shot-score",
            title: `SHOT SCORE ${score} %`,
            body: `Splnené: ${breakdown}. Skóre meria exekúciu makléra, nie rozhodnutie klienta.`,
            shotScoreId
          }, now),
          ...caseItem.timeline
        ],
        updatedAt: now
      };
    }));
  };

  const wipeAllData = () => {
    queueAllDeletions(cases.map((caseItem) => caseItem.id));
    marketAnalysis.clearAllData();
    skipNextSaveRef.current = true;
    clearCases();
    setCases([]);
    setSelectedId(null);
    setQuery("");
    setTodayFilter("Všetko");
    setCaseFilter("Všetky");
    setTransactionFilter("Predaj");
    setPropertyFilter("Byt");
    setIsCreating(false);
    setNewCaseDraft(createEmptyDraft());
    setOutcomeDraft(null);
    setDataWipeStep(0);
    setScreen("cases");
  };

  if (cloudConfigured && !authReady) {
    return (
      <div className="app-shell auth-shell">
        <main className="login-screen">
          <section className="login-panel" aria-live="polite">
            <p className="eyebrow">FISPRO OS</p>
            <h1>Načítavam účet...</h1>
          </section>
        </main>
      </div>
    );
  }

  if (cloudConfigured && !session) {
    return <LoginScreen onLogin={login} />;
  }

  if (cloudConfigured && passwordRecovery && session) {
    return (
      <PasswordRecoveryScreen
        onUpdatePassword={updatePassword}
        onComplete={completePasswordRecovery}
      />
    );
  }

  return (
    <MarketLocationProvider session={session} onSessionChange={adoptSession}>
      <MarketAnalysisContext.Provider value={marketAnalysis}>
        <CaseImageProvider cases={cases}>
        <div className="app-shell">
        <AppHeader screen={screen} onBackToCases={() => setScreen("cases")} />

        <main className="main-content">
        {(screen === "now" || screen === "today") ? (
          <DebriefInbox
            items={screen === "now" ? pendingDebriefs.slice(0, 1) : pendingDebriefs}
            now={operationalNow}
            onStart={openDebrief}
            onSnooze={snoozeDebrief}
            onNotRequired={openDebrief}
          />
        ) : null}
        {screen === "now" && pendingDebriefs.length === 0 && (
          <NowScreen
            caseItem={nextScheduledCase}
            today={today}
            onStartColdCall={startNewCase}
            onOpenCase={openCase}
            onOpenWaitingResult={openWaitingResult}
            onSelectOutcome={beginNowOutcome}
            onCompleteTask={(caseId) => { completeFollowUp(caseId); }}
          />
        )}

        {screen === "today" && (
          <TodayScreen
            cases={todayCases}
            allCases={cases}
            activeCases={activeCases.length}
            totalPotential={totalPotential}
            hotPotential={hotPotential}
            negotiationPotential={negotiationPotential}
            today={today}
            todayFilter={todayFilter}
            todayCounts={todayCounts}
            onTodayFilterChange={setTodayFilter}
            onOpenCase={openCase}
            onAddNextStep={openCaseForNextStep}
            onAddFirstCase={startNewCase}
          />
        )}

        {screen === "cases" && (
          <CasesScreen
            cases={filteredCases}
            query={query}
            isCreating={isCreating}
            draft={newCaseDraft}
            totalCases={caseFilter === "Archív" ? archivedCases.length : liveCases.length}
            caseFilter={caseFilter}
            transactionFilter={transactionFilter}
            propertyFilter={propertyFilter}
            onCaseFilterChange={setCaseFilter}
            onTransactionFilterChange={setTransactionFilter}
            onPropertyFilterChange={setPropertyFilter}
            onQueryChange={setQuery}
            onOpenCase={openCase}
            onToggleCreate={toggleCreateCase}
            onAddFirstCase={startNewCase}
            onDraftChange={setNewCaseDraft}
            onCreateCase={createCase}
          />
        )}

        {screen === "detail" && selectedCase && selectedPendingRequiredDebrief ? (
          <CaseDebriefGateScreen
            caseItem={selectedCase}
            reminder={selectedPendingRequiredDebrief}
            now={operationalNow}
            onStart={openDebrief}
            onSnooze={snoozeDebrief}
            onNotRequired={openDebrief}
          />
        ) : screen === "detail" && selectedCase ? (
          <CaseDetailScreen
            caseItem={selectedCase}
            outcomeDraft={outcomeDraft}
            outcomeConfig={outcomeConfig}
            today={today}
            onBeginOutcome={beginOutcome}
            onOutcomeDraftChange={setOutcomeDraft}
            onSaveOutcome={saveOutcome}
            onWaitAfterRejection={waitAfterRejection}
            onWaitingOutcome={saveWaitingOutcome}
            onNotesChange={updateNotes}
            onCaseChange={updateCase}
            onStageChange={updateStage}
            onReservationOutcome={resolveReservation}
            onFollowUpChange={updateFollowUp}
            onFollowUpComplete={completeFollowUp}
            onFollowUpCompleteWithNext={completeFollowUpWithNext}
            onOwnerSaleContactComplete={completeOwnerSaleContact}
            onOwnerSaleSequenceEnd={endOwnerSaleSequence}
            onTogglePriority={togglePriority}
            onCloseCase={closeCase}
            onSellCase={sellCase}
            onRejectCase={rejectCase}
            onDeleteCase={deleteCase}
            onRestoreCase={restoreCase}
            onShotScoreSave={saveShotScore}
            onMarketStatsSave={saveMarketStats}
            pendingRequiredDebrief={undefined}
            now={operationalNow}
            onOpenDebrief={openDebrief}
            onSnoozeDebrief={snoozeDebrief}
            openNextStepOnMount={nextStepIntentId === selectedCase.id}
            openWaitingResultOnMount={waitingResultIntentId === selectedCase.id}
            onNextStepIntentHandled={() => { setNextStepIntentId(null); setWaitingResultIntentId(null); }}
          />
        ) : null}

        {screen === "pipeline" && <PipelineScreen cases={cases} onOpenCase={openCase} />}

        {screen === "settings" && (
          <SettingsScreen
            totalCases={cases.length}
            activeCaseCount={lifecycleCounts.active}
            waitingCaseCount={lifecycleCounts.waiting}
            excludedCaseCount={lifecycleCounts.excluded}
            dataWipeStep={dataWipeStep}
            accountEmail={session?.user.email}
            cloudConfigured={cloudConfigured}
            syncStatus={syncStatus}
            notificationStatus={notificationStatus}
            notificationPermission={notificationPermission}
            notificationError={notificationError}
            notificationMessage={notificationMessage}
            notificationsWorking={notificationsWorking}
            notificationDeviceRegistered={notificationDeviceRegistered}
            notificationLastSuccessfulPushAt={lastSuccessfulPushAt}
            notificationLastFailureAt={notificationLastFailureAt}
            notificationLastErrorCategory={notificationLastErrorCategory}
            isInstalledPwa={isInstalledPwa}
            isIosDevice={isIosDevice}
            shotScoreAnalytics={shotScoreAnalytics}
            onEnableNotifications={() => void enableNotifications()}
            onDisableNotifications={() => void disableNotifications()}
            onSendTestNotification={() => void sendTestNotification()}
            onLogout={() => void logoutWithPushCleanup()}
            onStartWipe={() => setDataWipeStep(1)}
            onAdvanceWipe={() => setDataWipeStep(2)}
            onCancelWipe={() => setDataWipeStep(0)}
            onConfirmWipe={wipeAllData}
          />
        )}
        </main>

        {callPromptCase ? (
          <CallFirstPrompt caseItem={callPromptCase} onClose={() => setCallPromptCaseId(null)} />
        ) : null}

        {activeDebrief ? (
          <DebriefSheet
            item={activeDebrief}
            busy={debriefBusy}
            error={debriefError}
            today={today}
            requiresNextStep={activeDebriefNeedsNextStep}
            existingNextStep={!activeDebriefNeedsNextStep && activeDebrief.caseItem.followUp ? nextStepLabel(activeDebrief.caseItem.followUp) : undefined}
            onClose={() => setDebriefIntent(null)}
            onAnalyze={(text) => void analyzeDebrief(text)}
            onAnalyzeAudio={(audio, durationSeconds) => void analyzeVoiceDebrief(audio, durationSeconds)}
            onDraftChange={saveDebriefDraft}
            onConfirm={confirmDebrief}
            onManualOutcome={confirmManualMeetingOutcome}
            onNotRequired={(reason, note) => markDebriefNotRequired(activeDebrief.caseItem.id, activeDebrief.reminder.id, reason, note)}
            onMeetingDidNotHappen={(reason, note, reschedule) => resolveMissedMeeting(activeDebrief.caseItem.id, activeDebrief.reminder.id, reason, note, reschedule)}
          />
        ) : null}
        {debriefConfirmation ? <div className="debrief-confirmation" role="status">{debriefConfirmation}</div> : null}

        <BottomNavigation screen={screen} onNavigate={setScreen} />
        </div>
        </CaseImageProvider>
      </MarketAnalysisContext.Provider>
    </MarketLocationProvider>
  );
}

export default App;
